import { useEffect } from 'react';

import { SESSION_EVENTS } from '@/event-manager/types';
import { isEmbeddedInLobby } from '@/logic/embedded-mode';
import { refreshExternalAccountSession } from '@/unified-modules/account/external-session-refresh';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';

const isLobbySessionMessage = (
  data: unknown,
): data is { type: string; payload?: { jwt?: unknown } } =>
  typeof data === 'object' && data !== null && 'type' in data;

/**
 * Same-origin embedded: after lobby `syncCookiesUM`, casino-next posts AW/UPDATE_TOKENS
 * (e.g. ~13m keep-alive). Payload JWT is a signal only; cookies are already on the lobby origin.
 * AW/SESSION_INVALIDATED is logged only — lobby owns logout; GW stays on Account UM subscribe.
 */
export const useEmbeddedLobbySessionUpdate = (): void => {
  useEffect(() => {
    if (!isEmbeddedInLobby()) {
      return;
    }

    const handleMessage = async (
      event: MessageEvent<unknown>,
    ): Promise<void> => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.source !== window.parent) {
        return;
      }

      if (!isLobbySessionMessage(event.data)) {
        return;
      }

      if (event.data.type === SESSION_EVENTS.SESSION_INVALIDATED) {
        Logger.logRumAction({
          name: '[GW][SESSION] Embedded external session invalidated',
          useCase: DdEventCategory.Session,
          eventType: DdEventType.Action,
        });
        return;
      }

      if (event.data.type !== SESSION_EVENTS.TOKENS_UPDATE) {
        return;
      }

      void refreshExternalAccountSession();
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
};
